import React, { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ConditionMeansChart from "./ConditionMeansChart";
import FrequencyChart from "./FrequencyChart";
import DistributionChart from "./DistributionChart";
import ThermometerGapChart from "./ThermometerGapChart";
import ValenceSpectrumChart from "./ValenceSpectrumChart";

const MANIP_CHECK_EXPECTATIONS = {
  emotional_valence: "Expected: negative in outgroup, positive in ingroup, neutral in issue-focused",
  mc_emotion: "Expected: higher in collapsed (emotional) conditions",
  mc_attr: "Expected: adversary-naming higher in outgroup, ingroup identity higher in ingroup — independent of emotional register (hostility/warmth-free)",
  mc_ingroup: "Expected: solidarity highest in ingroup, but as an affective construal it should also rise with emotional register",
  mc_outgroup: "Expected: perceived hostility rises with emotional register; adversarial construal needs collapse, not just naming (see mc_attr)",
};

const ConstructCard = ({ construct, conditions, isManipCheck = false }) => {
  const [expanded, setExpanded] = useState(true);
  const [showPerItem, setShowPerItem] = useState(false);
  const [showDistribution, setShowDistribution] = useState({});

  // Detect thermometer pair
  const questions = construct.questions || [];
  const thermSupport = questions.find((q) => q.questionId === "therm_support");
  const thermOppose = questions.find((q) => q.questionId === "therm_oppose");
  const hasThermPair = thermSupport && thermOppose;

  return (
    <div
      className={`bg-gray-800 rounded-lg overflow-hidden ${
        isManipCheck ? "border-l-4 border-yellow-400" : ""
      }`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-750 transition-colors"
      >
        <div className="text-left">
          <h4 className="text-white font-medium text-sm">{construct.label}</h4>
          <p className="text-gray-500 text-xs mt-0.5">
            {construct.phase === "pre" ? "Pre-survey" : "Post-survey"}
            {" — "}
            {construct.questions.length} question{construct.questions.length !== 1 ? "s" : ""}
          </p>
        </div>
        {expanded ? (
          <FaChevronUp className="text-gray-400 w-3 h-3" />
        ) : (
          <FaChevronDown className="text-gray-400 w-3 h-3" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {construct.questions.map((q) => (
            <div key={q.questionId} className="border-t border-gray-700 pt-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-gray-300 text-sm font-medium">{q.label}</p>
                  {isManipCheck && MANIP_CHECK_EXPECTATIONS[q.questionId] && (
                    <p className="text-yellow-400 text-xs mt-1">
                      {MANIP_CHECK_EXPECTATIONS[q.questionId]}
                    </p>
                  )}
                </div>
                {q.type === "likert_battery" && q.items?.length > 1 && (
                  <div className="flex gap-3 ml-2">
                    <button
                      onClick={() => setShowPerItem(!showPerItem)}
                      className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                    >
                      {showPerItem ? "Show aggregate" : "Show per item"}
                    </button>
                    {q.valueDistribution && !showPerItem && (
                      <button
                        onClick={() => setShowDistribution((prev) => ({ ...prev, [q.questionId]: !prev[q.questionId] }))}
                        className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                      >
                        {showDistribution[q.questionId] ? "Show means" : "Show distribution"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {q.type === "likert_battery" && (
                <>
                  {showPerItem ? (
                    <div className="space-y-3">
                      {q.items.map((item) => (
                        <div key={item.itemId}>
                          <p className="text-gray-400 text-xs mb-1">
                            {item.text}
                            {item.reverse && (
                              <span className="text-yellow-500 ml-1">(R)</span>
                            )}
                          </p>
                          <ConditionMeansChart
                            stats={item.stats}
                            conditions={conditions}
                            scaleInfo={q.scaleInfo}
                            height={150}
                          />
                        </div>
                      ))}
                    </div>
                  ) : showDistribution[q.questionId] && q.valueDistribution ? (
                    <DistributionChart
                      valueDistribution={q.valueDistribution}
                      conditions={conditions}
                      scaleInfo={q.scaleInfo}
                      height={200}
                    />
                  ) : (
                    <ConditionMeansChart
                      stats={q.aggregateStats}
                      conditions={conditions}
                      scaleInfo={q.scaleInfo}
                      height={180}
                    />
                  )}
                  <ScaleLabelsRow scaleInfo={q.scaleInfo} />
                </>
              )}

              {["slider", "button_scale"].includes(q.type) && (
                <>
                  {/* Thermometer gap chart — replaces individual thermometer bars */}
                  {hasThermPair && (q.questionId === "therm_support") && (
                    <ThermometerGapChart
                      supportStats={thermSupport.stats}
                      opposeStats={thermOppose.stats}
                      conditions={conditions}
                      height={Math.max(200, conditions.length * 45)}
                    />
                  )}
                  {hasThermPair && (q.questionId === "therm_oppose") && null}

                  {/* Emotional valence — bipolar spectrum chart */}
                  {q.questionId === "emotional_valence" && (
                    <ValenceSpectrumChart
                      stats={q.stats}
                      conditions={conditions}
                      height={Math.max(200, conditions.length * 40)}
                    />
                  )}

                  {/* Regular slider/button_scale — with optional distribution view */}
                  {!(hasThermPair && ["therm_support", "therm_oppose"].includes(q.questionId)) && q.questionId !== "emotional_valence" && (
                    <>
                      {q.valueDistribution && (
                        <button
                          onClick={() => setShowDistribution((prev) => ({ ...prev, [q.questionId]: !prev[q.questionId] }))}
                          className="text-xs text-blue-400 hover:text-blue-300 mb-2 block"
                        >
                          {showDistribution[q.questionId] ? "Show means" : "Show distribution"}
                        </button>
                      )}
                      {showDistribution[q.questionId] && q.valueDistribution ? (
                        <DistributionChart
                          valueDistribution={q.valueDistribution}
                          conditions={conditions}
                          scaleInfo={q.scaleInfo}
                          height={200}
                        />
                      ) : (
                        <ConditionMeansChart
                          stats={q.stats}
                          conditions={conditions}
                          scaleInfo={q.scaleInfo}
                          height={180}
                        />
                      )}
                    </>
                  )}
                  <ScaleLabelsRow scaleInfo={q.scaleInfo} />
                </>
              )}

              {q.type === "single_choice" && (
                <FrequencyChart
                  frequencies={q.frequencies}
                  options={q.options}
                  conditions={conditions}
                  height={Math.max(150, (q.options?.length || 3) * 35)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ScaleLabelsRow = ({ scaleInfo }) => {
  if (!scaleInfo) return null;

  if (scaleInfo.minLabel || scaleInfo.maxLabel) {
    return (
      <div className="flex justify-between text-gray-500 text-xs mt-1 px-1">
        <span>{scaleInfo.min ?? 0}: {scaleInfo.minLabel}</span>
        <span>{scaleInfo.max ?? scaleInfo.points ?? 7}: {scaleInfo.maxLabel}</span>
      </div>
    );
  }

  if (scaleInfo.labels?.length > 0) {
    return (
      <div className="flex justify-between text-gray-500 text-xs mt-1 px-1">
        <span>1: {scaleInfo.labels[0]}</span>
        <span>{scaleInfo.labels.length}: {scaleInfo.labels[scaleInfo.labels.length - 1]}</span>
      </div>
    );
  }

  return null;
};

export default ConstructCard;
